import { Component, useEffect, useState, type ReactNode } from "react";
import { getToken, navigate, setToken, signInFor, useApi } from "./api";
import Auth from "./Auth";
import CatalogPage from "./Catalog";
import Checkout from "./Checkout";
import Home from "./Home";
import Orders from "./Orders";
import Profile, { Coupons, Invitations } from "./Profile";
import { Empty, Loading, Notice } from "./common";
import SupportChat from "./SupportChat";
import { setStorefrontTitle, unlockVisitorSupportSound } from "./visitor-support-notifications";
import { trackPage } from "./analytics";
import type { User } from "./types";
import { useProductCatalog } from "./useProductCatalog";
import { legacyProductSlug, productCatalog, storefrontLocation } from "./legacy-routes";

const links = [
  { path: "/subscribe", label: "订阅服务", icon: "◇" },
  { path: "/transaction-record", label: "我的订单", icon: "▤" },
  { path: "/coupons", label: "优惠券", icon: "▱" },
  { path: "/my-invitations", label: "我的邀请", icon: "♧" },
  { path: "/profile", label: "个人中心", icon: "○" },
];
function Information({ privacy }: { privacy: boolean }) {
  return (
    <article className="panel prose">
      <span className="eyebrow">PANDAI</span>
      <h1>{privacy ? "隐私政策" : "服务条款"}</h1>
      {privacy ? (
        <>
          <p>本页面说明 PandAI 店铺处理购买流程相关信息的方式。</p>
          <h2>账号与订单</h2>
          <p>
            下单需要填写查询邮箱与查询密码，用于之后查询订单和交付内容；也可以注册账号后登录购买。订单记录包含所购商品、金额、支付状态及交付内容；这些信息用于处理购买、交付和售后。
          </p>
          <h2>登录状态</h2>
          <p>
            登录凭据默认保存在当前浏览器会话中。勾选“在此设备保持登录”后，会保存在浏览器本地，直到退出或凭据失效。公共设备上请及时退出登录。
          </p>
          <h2>支付与联系</h2>
          <p>
            完成支付时，你将前往对应支付渠道的收银台。账号或资料相关问题，请通过在线客服联系我们。
          </p>
          <h2>访问统计与在线客服</h2>
          <p>店铺使用匿名浏览器标识统计访问来源、页面访问和下单转化，关联订单时仅用于店铺经营分析；不采集完整来源网址或支付凭据。匿名标识保存在本机浏览器，清除站点数据即可移除。在线客服会保存你发送的消息以便继续处理，请勿发送密码、验证码或完整支付资料；公共设备使用后请清除本机会话。</p>
        </>
      ) : (
        <>
          <p>在 PandAI 购买商品前，请阅读对应套餐和使用说明。</p>
          <h2>商品与价格</h2>
          <p>
            不同商品的交付内容、适用范围和期限可能不同。所有售价以人民币显示，最终支付金额以创建的订单及收银台为准。库存以提交订单时的核验结果为准。
          </p>
          <h2>支付与交付</h2>
          <p>
            请从订单入口完成支付，并在“我的订单”中查看服务端确认的状态和交付内容。未显示支付成功时，请勿仅根据跳转页面判断已完成。
          </p>
          <h2>使用与售后</h2>
          <p>
            请按商品说明使用并保管交付内容。遇到问题请通过在线客服联系我们，并提供订单号；处理结果以订单核验和客服确认为准。
          </p>
          <h2>第三方服务</h2>
          <p>
            商品中涉及的服务名称和标识属于其各自权利人。其服务条款、可用范围和限制以相应服务提供方的说明为准。
          </p>
        </>
      )}
      <button className="button subtle" onClick={() => navigate("/subscribe")}>
        返回订阅服务
      </button>
    </article>
  );
}

function Storefront() {
  const [locationKey, setLocationKey] = useState(
      location.pathname + location.search,
    ),
    [token, setAuthToken] = useState(getToken());
  const openSupport = () => window.dispatchEvent(new Event("pandai-open-support"));
  const resolvedLocation = storefrontLocation(locationKey),
    path = resolvedLocation.split("?")[0],
    params = new URLSearchParams(resolvedLocation.split("?")[1] || ""),
    productSlug = legacyProductSlug(path),
    isCatalogPage = path === "/subscribe" || productSlug !== null,
    navPath = isCatalogPage ? "/subscribe" : path;
  useEffect(() => {
    const route = () => setLocationKey(location.pathname + location.search),
      auth = () => setAuthToken(getToken());
    addEventListener("popstate", route);
    addEventListener("panda-auth", auth);
    addEventListener("storage", auth);
    return () => {
      removeEventListener("popstate", route);
      removeEventListener("panda-auth", auth);
      removeEventListener("storage", auth);
    };
  }, []);
  useEffect(() => {
    if (resolvedLocation === locationKey) return;
    history.replaceState({}, "", resolvedLocation + location.hash);
    setLocationKey(resolvedLocation);
  }, [locationKey, resolvedLocation]);
  useEffect(() => {
    setStorefrontTitle(
      (links.find((l) => l.path === path)?.label || "AI 订阅与成品账号") +
      " · PandAI");
  }, [path]);
  useEffect(() => { trackPage(locationKey); }, [locationKey]);
  const user = useApi<User>(token ? "/user/info" : null, token);
  const catalog = useProductCatalog(isCatalogPage);
  const shownCatalog = catalog.data && productSlug !== null
    ? productCatalog(catalog.data, productSlug) : catalog.data;
  // 2026-09-26 起下单、查单不强制登录：结算与订单页对游客开放，
  // 优惠券 / 邀请 / 个人中心仍需要账号。
  const publicRoute = [
    "/",
    "/subscribe",
    "/login",
    "/register",
    "/privacy-policy",
    "/terms-of-service",
    "/checkout",
    "/transaction-record",
    "/orders",
    "/payment/success",
    "/pay-result",
  ].includes(path) || productSlug !== null || path.startsWith("/orders/");
  function logout() {
    setToken("");
    navigate("/login");
  }
  if (path === "/")
    return (
      <Home
        loggedIn={!!token}
        onStart={() => navigate("/subscribe")}
        onLogin={() => navigate(token ? "/profile" : "/login")}
      />
    );
  if (path === "/login" || path === "/register")
    return (
      <Auth
        key={path}
        register={path === "/register"}
        onSuccess={() => user.reload()}
      />
    );
  let content: ReactNode;
  if (!publicRoute && !token)
    content = (
      <Empty
        title="登录后继续"
        text="登录即可使用优惠券、查看邀请奖励与账户信息。"
        action={
          <button
            className="button primary"
            onClick={() => signInFor(locationKey)}
          >
            登录 / 注册
          </button>
        }
      />
    );
  else if (!publicRoute && user.loading) content = <Loading />;
  else if (!publicRoute && user.error)
    content = (
      <>
        <Notice>{user.error}</Notice>
        <button className="button subtle" onClick={user.reload}>
          重试
        </button>
        <button className="button subtle" onClick={logout}>
          重新登录
        </button>
      </>
    );
  else if (isCatalogPage)
    content = productSlug !== null && shownCatalog && !shownCatalog.plans.length && !catalog.error ? (
      <Empty
        title="商品已下架或链接已更新"
        text="可以在订阅服务中查看当前上架的商品。"
        action={<button className="button primary" onClick={() => navigate("/subscribe")}>查看订阅服务</button>}
      />
    ) : (
      <CatalogPage
        key={productSlug ?? "catalog"}
        catalog={shownCatalog}
        loading={catalog.loading}
        error={catalog.error}
        onRetry={catalog.reload}
        onSelect={(plan) => navigate("/checkout?plan=" + encodeURIComponent(plan.id))}
      />
    );
  else if (path === "/checkout")
    content = (
      <Checkout key={params.get("plan")} planId={params.get("plan") || ""} loggedIn={!!token} />
    );
  else if (path === "/transaction-record" || path === "/orders")
    content = <Orders loggedIn={!!token} />;
  else if (path.startsWith("/orders/"))
    content = (
      <Orders key={path} orderNo={decodeURIComponent(path.slice(8))} loggedIn={!!token} />
    );
  else if (path === "/payment/success" || path === "/pay-result")
    content = (
      <Orders
        key={locationKey}
        orderNo={params.get("order_id") || params.get("orderNo") || undefined}
        paymentReturn
        loggedIn={!!token}
      />
    );
  else if (path === "/profile" && user.data)
    content = <Profile user={user.data} onPasswordChanged={logout} />;
  else if (path === "/coupons") content = <Coupons />;
  else if (path === "/my-invitations") content = <Invitations />;
  else if (path === "/privacy-policy" || path === "/terms-of-service")
    content = <Information privacy={path === "/privacy-policy"} />;
  else
    content = (
      <Empty
        title="页面未找到"
        text="这个页面可能已调整。"
        action={
          <button
            className="button primary"
            onClick={() => navigate("/subscribe")}
          >
            返回服务列表
          </button>
        }
      />
    );
  return (
    <div className="app-shell">
      <header className="app-header">
        <button className="brand-button" onClick={() => navigate("/")}>
          <img src="/pandai-wordmark.png" alt="PandAI 首页" width={1942} height={809} />
        </button>
        <div className="header-actions">
          <button className="header-link" onClick={openSupport}>
            联系客服
          </button>
          {token ? (
            <>
              <button
                className="account-button"
                onClick={() => navigate("/profile")}
              >
                <span className="avatar">
                  {(user.data?.nickname || user.data?.email || "P")
                    .slice(0, 1)
                    .toUpperCase()}
                </span>
                <span>
                  {user.data?.nickname || user.data?.email || "我的账号"}
                </span>
              </button>
              <button className="header-link logout" onClick={logout}>
                退出
              </button>
            </>
          ) : (
            <button
              className="button primary small"
              onClick={() => signInFor(locationKey)}
            >
              登录 / 注册
            </button>
          )}
        </div>
      </header>
      <aside className="sidebar">
        <nav aria-label="主导航">
          {links.map((link) => (
            <button
              key={link.path}
              className={
                navPath === link.path ||
                (link.path === "/transaction-record" &&
                  path.startsWith("/orders/"))
                  ? "active"
                  : ""
              }
              aria-current={navPath === link.path ? "page" : undefined}
              onClick={() => navigate(link.path)}
            >
              <span className="nav-icon" aria-hidden="true">
                {link.icon}
              </span>
              {link.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <button onClick={openSupport}>
            帮助与售后 <span>↗</span>
          </button>
          <p>PandAI · pandai.shop</p>
          <a href="/terms-of-service">服务条款</a>
          <span> · </span>
          <a href="/privacy-policy">隐私政策</a>
        </div>
      </aside>
      <div className="workspace" key={path + token}>
        <div
          role={isCatalogPage ? undefined : "main"}
          className={isCatalogPage ? "catalog-content" : "page-content"}
        >
          {content}
        </div>
      </div>
    </div>
  );
}
function SupportDock() {
  const [open, setOpen] = useState(false), [unread, setUnread] = useState(0);
  const show = () => { void unlockVisitorSupportSound(true); setOpen(true); };
  useEffect(() => {
    addEventListener("pandai-open-support", show);
    return () => removeEventListener("pandai-open-support", show);
  }, []);
  return <>{!open && <button className="support-dock" onClick={show} aria-label={unread ? `打开在线客服，${unread} 条未读回复` : "打开在线客服"}>
    <span aria-hidden="true">◌</span> 在线客服
    {unread > 0 && <span className="support-unread-badge">{unread > 99 ? "99+" : unread}</span>}
  </button>}<SupportChat open={open} onClose={() => setOpen(false)} onUnreadChange={setUnread} /></>;
}
export default class App extends Component<object, { error: boolean }> {
  state = { error: false };
  static getDerivedStateFromError() {
    return { error: true };
  }
  render() {
    return this.state.error ? (
      <div className="fatal">
        <h1>页面加载遇到问题</h1>
        <p>请刷新页面后重试。</p>
        <button className="button primary" onClick={() => location.reload()}>
          刷新页面
        </button>
      </div>
    ) : (
      <><Storefront /><SupportDock /></>
    );
  }
}
